import { useEffect, useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import LoadingPopup from '@/shared/components/LoadingPopup/LoadingPopup';
import { AttorneyPage as Attorney } from './features/attorney';
import { AdminPage as Admin } from './features/admin';
import { hasAnyAdminAccess } from '@/shared/config/adminPermissions';
import { logger } from '@/shared/utils/logger';

type AppView = 'admin' | 'attorney';

// SP-initiated SSO: redirect users from app.hourglass.law to their tenant subdomain
const ORG_TENANT_URLS: Record<string, string> = {
  "org_01KH4HH2TTWWGJMS2ZAVV73RC4": "https://namanhowell.hourglass.law",
  "org_01KBR4JTS2NNBHT2KRGHZ9VVVR": "https://taylordaylaw.hourglass.law",
  "org_01K7RGPDSVQHZVZY2MKBDKRQ8M": "https://hourglass.pregodonnell.com",
  "org_01K7JD4563CA4ED2KE4721Y1ZM": "https://hourglass.bpmlaw.com",
  "org_01K7PTWAR69ZHVQTAX9T1BFT7N": "https://hwslawgroup.hourglass.law",
  "org_01KA1NGHMEE35HNYS4AV8ASYE1": "https://hourglass.tysonmendes.com",
};

export default function App() {
  const { isLoading, user, organizationId, signIn } = useAuth();
  const [currentView, setCurrentView] = useState<AppView | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Automatically sign in the user if they're not authenticated
  useEffect(() => {
    if (!user && !isLoading) {
      signIn(); // Automatically triggers the sign-in flow
    }
  }, [user, isLoading, signIn]);

  // Redirect to tenant subdomain if on app.hourglass.law after SP-initiated SSO
  useEffect(() => {
    if (!user || !organizationId) return;
    if (window.location.hostname !== 'app.hourglass.law') return;

    const tenantUrl = ORG_TENANT_URLS[organizationId];
    if (tenantUrl) {
      setIsRedirecting(true);
      window.location.href = tenantUrl;
    }
  }, [user, organizationId]);

  // Set user context for Sentry error tracking
  useEffect(() => {
    if (user) {
      logger.setUser({
        id: user.id,
        email: user.email,
        username: user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.email,
      });
    }
  }, [user]);

  // Determine initial view based on admin status
  useEffect(() => {
    if (user && currentView === null) {
      const isAdmin = hasAnyAdminAccess(user.email);
      // Default to admin view if user has admin access, otherwise attorney
      setCurrentView(isAdmin ? 'admin' : 'attorney');
    }
  }, [user, currentView]);

  // Show a loading spinner while checking user state or during sign-in
  if (isLoading || !user || currentView === null || isRedirecting) {
    return <LoadingPopup show={true} />;
  }

  // Render based on current view
  if (currentView === 'admin') {
    return (
      <Admin
        onSwitchToAttorney={() => setCurrentView('attorney')}
      />
    );
  }

  return (
    <Attorney
      onSwitchToAdmin={
        hasAnyAdminAccess(user.email)
          ? () => setCurrentView('admin')
          : undefined
      }
    />
  );
}
